import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Subscription, map } from 'rxjs';
import { AccountsService } from '../../../../core/services/accounts.service';
import { FormArray, FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DialogService, NotifierService, User, UserService } from '@wm-libs/core';
import { Account } from '../../../../core/interfaces/accounts.interface';
import { TranslateModule } from '@ngx-translate/core';
import { EmptyDataComponent } from '../../../../core/components/empty-data/empty-data.component';
import { MatInputModule } from '@angular/material/input';
import { NgClass, NgIf, NgFor, NgTemplateOutlet, AsyncPipe, UpperCasePipe } from '@angular/common';

@Component({
  selector: 'app-account',
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss'],
  standalone: true,
  imports: [NgClass, NgIf, NgFor, FormsModule, ReactiveFormsModule, MatInputModule, NgTemplateOutlet, EmptyDataComponent, AsyncPipe, TranslateModule, UpperCasePipe],
})
export class AccountComponent implements OnInit, OnDestroy {
  objectKeys = Object.keys;
  farmNames!: FormArray;
  userData$!: User | null;

  accounts!: Account[] | null;
  userDataSubscription!: Subscription;
  accountsSubscription!: Subscription;

  isMobile$ = this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(map((result) => result.matches));

  constructor(
    public dialog: MatDialog,
    private fb: FormBuilder,
    private breakpointObserver: BreakpointObserver,
    private accountsService: AccountsService,
    private userService: UserService,
    private notifier: NotifierService,
    private cdr: ChangeDetectorRef,
    private dialogService: DialogService
  ) {}

  ngOnInit(): void {
    this.userDataSubscription = this.userService.userData$.subscribe((userData) => {
      this.userData$ = userData;
    });
    this.accountsSubscription = this.accountsService.accounts$.subscribe((accounts) => {
      this.accounts = accounts;

      if (!this.accounts?.length) {
        return;
      }

      this.updateHighlightedAccount(this.accounts);
    });
  }

  updateHighlightedAccount(accounts: Account[]) {
    this.farmNames = this.fb.array(accounts.map((account) => this.fb.group({ label: account.label })));

    this.cdr.detectChanges();

    const currentElement = document.querySelector('.current') as HTMLElement;

    if (currentElement) {
      this.scrollElement(currentElement);
    }
  }

  addAccount() {
    this.notifier.setNotifications('POOL.CLICK.CREATE_FARM');
  }

  switchAccount(account: Account, event: MouseEvent) {
    this.scrollToItem(event);

    if (account.current) {
      return;
    }
    const id = account.id;

    this.accountsService.switchAccount(id).subscribe();
  }

  updateAccount(label: string, id: number) {
    if (/^\s*$/.test(label)) {
      label = '';
    }

    this.accountsService.updateAccount(label, id).subscribe();
  }

  editFarmName(account: Account) {
    const config = {
      title: 'WM_TECH_POOL.ACCOUNT.CHANGE_FARMNAME',
      app: 'pool',
      fields: [{ name: 'farmName', label: 'WM_TECH_POOL.ACCOUNT.FARMNAME', type: 'input', value: account.label || '', validators: [Validators.required] }],
    };

    const dialogRef = this.dialogService.openPopup(config);

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.farmName) {
        this.updateAccount(result.farmName, account.id);
      }
    });
  }

  scrollElement(element: HTMLElement) {
    this.isMobile$.subscribe((isMobile) => {
      if (!isMobile) {
        return;
      }

      if (element) {
        const parent = element.parentElement;

        if (parent) {
          const scrollLeft = element.offsetLeft - 34;

          parent.scrollTo({
            left: scrollLeft,
            behavior: 'smooth',
          });
        }
      }
    });
  }

  scrollToItem(event: MouseEvent) {
    const element = (event.target as HTMLElement).closest('div');

    if (element) {
      this.scrollElement(element);
    }
  }

  ngOnDestroy(): void {
    if (this.accountsSubscription) this.accountsSubscription.unsubscribe();
  }
}
